import { ArrowNarrowLeft, ArrowUp, AtSign, Check, ChevronRight, Code02, Cube01, Hand, Paperclip, Plus, Upload01, XClose, Zap } from '@untitled-ui/icons-react/build/cjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ButtonCmp from 'src/components/ButtonCmp';
import OrbAvatar from 'src/components/OrbAvatar';
import TooltipCmp from 'src/components/TooltipCmp';

import AgentMentionsInput from './AgentMentionsInput';
import type { MentionCategory } from './AgentMentionsInput/agentMentionsInput.interface';

type AgentMode = 'auto' | 'semi_auto' | 'manual';

const itemClass = 'flex items-center gap-x-2 px-2 py-1.5 rounded-md hover:bg-utilityGray100 bg-white cursor-pointer text-left';

const renderCategoryIcon = (icon?: MentionCategory['icon']) => {
    if (!icon) return <Cube01 className="h-4 w-4 text-gray500 shrink-0" />;
    if (typeof icon === 'string') return <img src={icon} className="h-4 w-4 object-contain shrink-0" alt="" />;

    const Icon = icon;

    return <Icon className="h-4 w-4 text-gray500 shrink-0" />;
};

const MODE_OPTIONS = [
    { key: 'auto' as AgentMode, label: 'Auto', desc: 'Agent handles everything automatically', Icon: Zap },
    { key: 'semi_auto' as AgentMode, label: 'Semi Auto', desc: 'Agent works with your confirmation when needed', Icon: Code02 },
    { key: 'manual' as AgentMode, label: 'Manual', desc: 'You review and approve every step', Icon: Hand },
];

// Mock data — swap for real entity lookups once the backend is wired.
const MENTION_CATEGORIES: MentionCategory[] = [
    {
        id: 'quotes',
        label: 'Quotes',
        children: [
            { id: 'quote-tc3b-1753', label: '#WAL-TC3B-1753 — Milton, ON → Calgary, AB' },
            { id: 'quote-tc3b-2015', label: '#WAL-TC3B-2015 — Milton, ON → Calgary, AB' },
            { id: 'quote-hq-tc3b-854', label: '#WALHQ-TC3B-854 — Xpo Logistics Freight' },
        ],
    },
    {
        id: 'shipments',
        label: 'Shipments',
        children: [
            { id: 'shp-tc3b-1753', label: '#WAL-TC3B-1753 — Steeles Ave E, Milton, ON' },
            { id: 'shp-tc3b-2015', label: '#WAL-TC3B-2015 — Oki Dr NW, Calgary, AB' },
            { id: 'shp-hq-tc3b-854', label: '#WALHQ-TC3B-854 — Water St Manhattan, New York' },
        ],
    },
];

const AgentComposer = ({ onSend, disabled = false }: { onSend: (text: string) => void; disabled?: boolean }) => {
    const [value, setValue] = useState('');
    const [modeDropdownOpen, setModeDropdownOpen] = useState(false);
    const [selectedMode, setSelectedMode] = useState<AgentMode>('semi_auto');
    const modeDropdownRef = useRef<HTMLDivElement>(null);
    const modeDropdownMenuRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // "+" menu — Upload file / Reference
    const [plusMenuOpen, setPlusMenuOpen] = useState(false);
    const plusTriggerRef = useRef<HTMLDivElement>(null);
    const plusMenuPopupRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Reference picker — opens at the same position as the "+" menu, no "@" in the input.
    const [refPickerOpen, setRefPickerOpen] = useState(false);
    const [refPickerCategory, setRefPickerCategory] = useState<MentionCategory | null>(null);
    const [refPickerSelectedIdx, setRefPickerSelectedIdx] = useState(0);
    const refPickerRef = useRef<HTMLDivElement>(null);

    // Chips for @ mentions, reference picker selections, and attached files — rendered above the input row.
    const [chips, setChips] = useState<{ id: string; label: string; categoryIcon?: MentionCategory['icon'] }[]>([]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;

            if (!modeDropdownRef.current?.contains(target) && !modeDropdownMenuRef.current?.contains(target)) {
                setModeDropdownOpen(false);
            }

            if (!plusTriggerRef.current?.contains(target) && !plusMenuPopupRef.current?.contains(target)) {
                setPlusMenuOpen(false);
            }

            if (!plusTriggerRef.current?.contains(target) && !refPickerRef.current?.contains(target)) {
                setRefPickerOpen(false);
                setRefPickerCategory(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSubmit = () => {
        if (!value.trim() || disabled) return;
        onSend(value.trim());
        setValue('');
    };

    // "Upload file" in the "+" menu: no import infra exists yet in this project, so attaching
    // a file just adds it as a chip (paperclip icon) — swap for real upload wiring once available.
    const handleUploadClick = () => {
        setPlusMenuOpen(false);
        fileInputRef.current?.click();
    };

    const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];

        if (file) {
            setChips((prev) => [...prev, { id: `file-${file.name}-${file.size}`, label: file.name, categoryIcon: Paperclip }]);
        }

        e.target.value = '';
    };

    // "Reference" in the "+" menu: close the "+" menu and open the category/leaf picker
    // at the same position — no "@" is inserted into the textarea.
    const handleOpenRefPicker = () => {
        setPlusMenuOpen(false);
        setRefPickerCategory(null);
        setRefPickerSelectedIdx(0);
        setRefPickerOpen(true);
    };

    const handleRefLeafSelect = useCallback((leaf: MentionCategory['children'][number], category: MentionCategory) => {
        setChips((prev) => [...prev, { id: leaf.id, label: leaf.label, categoryIcon: category.icon }]);
        setRefPickerOpen(false);
        setRefPickerCategory(null);
        textareaRef.current?.focus();
    }, []);

    // Up/Down/Enter navigation for the reference picker popup, mirroring the "@" mention
    // dropdown's keyboard handling in AgentMentionsInput.
    useEffect(() => {
        if (!refPickerOpen) return;

        const items = refPickerCategory ? refPickerCategory.children : MENTION_CATEGORIES;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (items.length === 0) return;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setRefPickerSelectedIdx((p) => (p + 1) % items.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setRefPickerSelectedIdx((p) => (p - 1 + items.length) % items.length);
            } else if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault();

                if (refPickerCategory) {
                    handleRefLeafSelect(refPickerCategory.children[refPickerSelectedIdx], refPickerCategory);
                } else {
                    setRefPickerCategory(MENTION_CATEGORIES[refPickerSelectedIdx]);
                    setRefPickerSelectedIdx(0);
                }
            } else if (e.key === 'Escape') {
                e.preventDefault();

                if (refPickerCategory) {
                    setRefPickerCategory(null);
                    setRefPickerSelectedIdx(0);
                } else {
                    setRefPickerOpen(false);
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [refPickerOpen, refPickerCategory, refPickerSelectedIdx, handleRefLeafSelect]);

    const selectedModeOption = MODE_OPTIONS.find((o) => o.key === selectedMode) ?? MODE_OPTIONS[1];

    return (
        <div className="aiAgentInputWrap">
            <div className="aiAgentInputWrapAnimation px-3 py-2 space-y-2">
                {chips.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap max-h-[248px] overflow-y-auto scrollbar-hide">
                        {chips.map((chip, i) => (
                            <span
                                key={`${chip.id}-${i}`}
                                className="group inline-flex items-center gap-x-1 rounded-md px-1.5 py-0.5 text-utilityBlack800 text-xs leading-[1.5] font-medium bg-grayDark900"
                            >
                                <button
                                    type="button"
                                    className="flex-shrink-0 flex items-center justify-center w-3.5 h-3.5"
                                    onClick={() => setChips((prev) => prev.filter((_, idx) => idx !== i))}
                                    tabIndex={-1}
                                >
                                    <span className="group-hover:hidden flex items-center justify-center w-3.5 h-3.5 text-grayLight600">
                                        {chip.categoryIcon && typeof chip.categoryIcon !== 'string' ? (
                                            <chip.categoryIcon className="w-3.5 h-3.5 shrink-0" />
                                        ) : (
                                            <Cube01 className="w-3.5 h-3.5 shrink-0" />
                                        )}
                                    </span>
                                    <XClose className="hidden group-hover:block w-3.5 h-3.5 cursor-pointer" />
                                </button>
                                {chip.label}
                            </span>
                        ))}
                    </div>
                )}

                <div className="flex items-end gap-2 w-full relative">
                    <figure className="w-6 h-6 flex-shrink-0 absolute left-0 bottom-0 z-[1]">
                        <OrbAvatar size={24} />
                    </figure>

                    <AgentMentionsInput
                        value={value}
                        onChange={setValue}
                        onSend={handleSubmit}
                        placeholder="Ask something"
                        disabled={disabled}
                        variables={MENTION_CATEGORIES}
                        inputRef={textareaRef}
                        className="flex-1 pl-7"
                        onVariableSelect={(leaf, category) => setChips((prev) => [...prev, { id: leaf.id, label: leaf.label, categoryIcon: category.icon }])}
                    />

                    <div className="flex items-center gap-x-2 absolute right-0 bottom-0 z-[1]">
                        <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileSelected} />

                        <div ref={plusTriggerRef} className="relative">
                            <ButtonCmp type="button" className="btn_neutral" size="circle-xxs" icon={<Plus className="h-4 w-4" />} onClick={() => setPlusMenuOpen((prev) => !prev)} disabled={disabled}>
                                <></>
                            </ButtonCmp>
                        </div>

                        <div ref={modeDropdownRef} className="relative">
                            <TooltipCmp childrenClassName="!flex" message={selectedModeOption.label}>
                                <ButtonCmp
                                    type="button"
                                    className="btn_neutral"
                                    size="circle-xxs"
                                    icon={
                                        selectedMode === 'auto' ? (
                                            <Zap className="h-4 w-4 shrink-0" />
                                        ) : selectedMode === 'semi_auto' ? (
                                            <Code02 className="h-4 w-4 shrink-0" />
                                        ) : (
                                            <Hand className="h-4 w-4 shrink-0" />
                                        )
                                    }
                                    onClick={() => setModeDropdownOpen((prev) => !prev)}
                                    disabled={disabled}
                                >
                                    <></>
                                </ButtonCmp>
                            </TooltipCmp>
                        </div>

                        <ButtonCmp type="button" className="btn_primary" size="circle-xxs" icon={<ArrowUp className="w-4 h-4" />} onClick={handleSubmit} disabled={!value.trim() || disabled}>
                            <></>
                        </ButtonCmp>
                    </div>
                </div>
            </div>

            {plusMenuOpen && (
                <div
                    ref={plusMenuPopupRef}
                    className="p-1.5 absolute right-0 bottom-[calc(100%_+_8px)] rounded-lg border border-utilityGray200 bg-white shadow-[0_0_10px_3px_rgba(16,24,40,0.04)] min-w-[160px] z-10"
                >
                    <ul role="listbox" aria-label="Composer actions">
                        <li role="option" aria-selected={false} tabIndex={0} className={itemClass} onClick={handleUploadClick}>
                            <Upload01 className="h-4 w-4 text-gray500 shrink-0" />
                            <p className="text-xs leading-[1.5] font-medium text-textSecondary flex-1 whitespace-nowrap">Upload file</p>
                            <ChevronRight className="h-3.5 w-3.5 text-gray400 shrink-0" />
                        </li>
                        <li role="option" aria-selected={false} tabIndex={0} className={itemClass} onClick={handleOpenRefPicker}>
                            <AtSign className="h-4 w-4 text-gray500 shrink-0" />
                            <p className="text-xs leading-[1.5] font-medium text-textSecondary flex-1 whitespace-nowrap">Reference</p>
                            <ChevronRight className="h-3.5 w-3.5 text-gray400 shrink-0" />
                        </li>
                    </ul>
                </div>
            )}

            {refPickerOpen && (
                <div
                    ref={refPickerRef}
                    className="p-1.5 absolute right-0 bottom-[calc(100%_+_8px)] rounded-lg border border-utilityGray200 bg-white shadow-[0_0_10px_3px_rgba(16,24,40,0.04)] min-w-[160px] z-10"
                    role="dialog"
                    aria-label="Reference picker"
                >
                    {refPickerCategory ? (
                        <>
                            <button
                                type="button"
                                className={`${itemClass} text-grayLight600 w-full`}
                                onClick={() => {
                                    setRefPickerCategory(null);
                                    setRefPickerSelectedIdx(0);
                                }}
                            >
                                <ArrowNarrowLeft className="h-4 w-4 shrink-0" />
                                <span className="flex-1 text-xs font-medium">Modules</span>
                            </button>
                            <ul>
                                {refPickerCategory.children.map((leaf, idx) => (
                                    <li key={leaf.id}>
                                        <button
                                            type="button"
                                            className={`${itemClass} w-full ${idx === refPickerSelectedIdx ? '!bg-utilityGray100' : ''}`}
                                            onClick={() => handleRefLeafSelect(leaf, refPickerCategory)}
                                            onMouseEnter={() => setRefPickerSelectedIdx(idx)}
                                        >
                                            {renderCategoryIcon(refPickerCategory.icon)}
                                            <span className="flex-1 truncate text-xs leading-[1.5] font-medium text-textSecondary whitespace-nowrap">{leaf.label}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </>
                    ) : (
                        <>
                            <button
                                type="button"
                                className={`${itemClass} text-grayLight600 w-full`}
                                onClick={() => {
                                    setRefPickerOpen(false);
                                    setPlusMenuOpen(true);
                                }}
                            >
                                <ArrowNarrowLeft className="h-4 w-4 shrink-0" />
                                <span className="flex-1 text-xs font-medium">Modules</span>
                            </button>
                            <ul role="listbox" aria-label="Reference categories">
                                {MENTION_CATEGORIES.map((cat, idx) => (
                                    <li key={cat.id} role="option" aria-selected={idx === refPickerSelectedIdx}>
                                        <button
                                            type="button"
                                            className={`${itemClass} w-full ${idx === refPickerSelectedIdx ? '!bg-utilityGray100' : ''}`}
                                            onClick={() => {
                                                setRefPickerCategory(cat);
                                                setRefPickerSelectedIdx(0);
                                            }}
                                            onMouseEnter={() => setRefPickerSelectedIdx(idx)}
                                        >
                                            {renderCategoryIcon(cat.icon)}
                                            <span className="flex-1 truncate text-xs leading-[1.5] font-medium text-textSecondary whitespace-nowrap">{cat.label}</span>
                                            <ChevronRight className="h-3.5 w-3.5 text-gray400 shrink-0" />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            )}

            {modeDropdownOpen && (
                <div
                    ref={modeDropdownMenuRef}
                    className="px-3 py-2.5 absolute w-full bottom-[calc(100%_+_8px)] rounded-lg border border-utilityGray200 bg-white shadow-[0_0_10px_3px_rgba(16,24,40,0.04)] space-y-2.5 z-10"
                >
                    <h6 className="text-sm font-medium text-grayLight900">Mode</h6>
                    <ul className="space-y-1.5" role="listbox" aria-label="Mode">
                        {MODE_OPTIONS.map(({ key, label, desc, Icon }) => (
                            <li
                                key={key}
                                role="option"
                                aria-selected={selectedMode === key}
                                tabIndex={0}
                                className={`flex gap-x-1 p-1.5 rounded-md hover:bg-utilityGray100 ${selectedMode === key ? 'bg-utilityGray100' : 'bg-white'} cursor-pointer`}
                                onClick={() => {
                                    setSelectedMode(key);
                                    setModeDropdownOpen(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setSelectedMode(key);
                                        setModeDropdownOpen(false);
                                    }
                                }}
                            >
                                <Icon className="mt-px h-4 w-4 text-gray500 shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs leading-[1.5] font-medium text-textSecondary">{label}</p>
                                    <p className="text-xs leading-[1.5] font-normal text-gray500">{desc}</p>
                                </div>
                                {selectedMode === key && <Check className="h-5 w-5 text-gray500 shrink-0 self-center" />}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default AgentComposer;
